import * as React from 'react';

import { type PaginationState } from '@tanstack/react-table';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useTableStore } from 'src/registry/providers/useTableStore';
import { tableStorage } from 'src/registry/utils/storage';

export const pageSizes = {
    10: 10,
    25: 25,
    50: 50,
    100: 100,
};

type UsePaginationProps = {
    pageIndex?: number;
    pageSize?: keyof typeof pageSizes;
};

function usePagination(props?: UsePaginationProps) {
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const { name } = useTableStore();
    // The route is the table's identity: two lists never share one, and a detail page linking
    // back to a bare list path (`/customers`) still resolves to the same key, which is what lets
    // the reader return to the page they left rather than to the first one.
    const storageKey = name ?? pathname;
    const [paginationState, setPaginationState] = React.useState<PaginationState>(() => {
        const searchParamsPage =
            searchParams.get('page') != null ? parseInt(searchParams.get('page') || '', 10) : undefined;
        const searchParamsSize =
            searchParams.get('size') != null ? parseInt(searchParams.get('size') || '', 10) : undefined;

        const tableStorageState = tableStorage.getTableState(storageKey) || {};
        const pageIndex = searchParamsPage ?? tableStorageState.pageIndex ?? props?.pageIndex ?? 0;
        const pageSize = searchParamsSize ?? tableStorageState.pageSize ?? props?.pageSize ?? 25;

        return { pageIndex, pageSize };
    });

    const queryPaginationOptions = React.useMemo(() => {
        return {
            limit: paginationState.pageSize,
            offset: paginationState.pageIndex * paginationState.pageSize,
        };
    }, [paginationState]);

    return { paginationState, setPaginationState, queryPaginationOptions, storageKey };
}

export default usePagination;
